import React, { createContext, useReducer, useContext, useEffect } from "react";

// First define the context
const CheckoutContext = createContext();

// --- Safe JSON parser ---
const safeParse = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    if (!value || value === "undefined") return fallback;
    return JSON.parse(value);
  } catch (err) {
    console.error(`Failed to parse ${key} from localStorage`, err);
    return fallback;
  }
};

// Load both cart and addresses from localStorage
const loadFromStorage = () => {
  return {
    cartItems: safeParse("cart", []),
    shippingDetails: safeParse("shippingAddresses", []),
    orders: safeParse("orders", []),
    appliedCoupon: safeParse("appliedCoupon", null),
  };
};

const initialState = {
  step: 1,
  appliedCoupon: null,
  ...loadFromStorage(),
};

function checkoutReducer(state, action) {
  let newState;

  switch (action.type) {
    case "SET_STEP":
      newState = { ...state, step: action.payload };
      break;

    case "ADD_SHIPPING_ADDRESS":
      const isDefault = action.payload.isDefault;
      const updatedAddresses = isDefault
        ? state.shippingDetails.map((addr) => ({ ...addr, isDefault: false }))
        : state.shippingDetails;

      newState = {
        ...state,
        shippingDetails: [...updatedAddresses, action.payload],
      };
      break;

    case "EDIT_SHIPPING_ADDRESS":
      newState = {
        ...state,
        shippingDetails: state.shippingDetails.map((addr) =>
          addr.id === action.payload.id
            ? { ...action.payload }
            : action.payload.isDefault
            ? { ...addr, isDefault: false }
            : addr
        ),
      };
      break;

    case "DELETE_SHIPPING_ADDRESS":
      newState = {
        ...state,
        shippingDetails: state.shippingDetails.filter(
          (addr) => addr.id !== action.payload.id
        ),
      };
      break;

    case "ADD_TO_CART":
      const existingItemIndex = state.cartItems.findIndex(
        (item) =>
          item.id === action.payload.id &&
          item.size === action.payload.size
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...state.cartItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity:
            updatedItems[existingItemIndex].quantity + action.payload.quantity,
        };
        newState = { ...state, cartItems: updatedItems };
      } else {
        newState = {
          ...state,
          cartItems: [...state.cartItems, action.payload],
        };
      }
      break;

    case "REMOVE_FROM_CART":
      newState = {
        ...state,
        cartItems: state.cartItems.filter(
          (item) =>
            !(
              item.id === action.payload.id && item.size === action.payload.size
            )
        ),
      };
      break;

    case "UPDATE_QUANTITY":
      newState = {
        ...state,
        cartItems: state.cartItems.map((item) =>
          item.id === action.payload.id && item.size === action.payload.size
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };
      break;

    case "CLEAR_CART":
      newState = {
        ...state,
        cartItems: [],
      };
      break;

    case "ADD_ORDER":
      newState = {
        ...state,
        orders: [...state.orders, action.payload],
      };
      break;

    case "CLEAR_ORDERS":
      newState = {
        ...state,
        orders: [],
      };
      break;

    case "SET_COUPON":
      newState = {
        ...state,
        appliedCoupon: action.payload,
      };
      break;
      newState = state;
  }

  return newState;
}

export function CheckoutProvider({ children }) {
  const [state, dispatch] = useReducer(checkoutReducer, initialState);

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(state.cartItems));
    localStorage.setItem(
      "shippingAddresses",
      JSON.stringify(state.shippingDetails)
    );
    localStorage.setItem("orders", JSON.stringify(state.orders));
    if (state.appliedCoupon) {
      localStorage.setItem("appliedCoupon", JSON.stringify(state.appliedCoupon));
    } else {
      localStorage.removeItem("appliedCoupon");
    }
  }, [state.cartItems, state.shippingDetails, state.orders, state.appliedCoupon]);

  return (
    <CheckoutContext.Provider value={{ state, dispatch }}>
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckout() {
  const context = useContext(CheckoutContext);
  if (!context) {
    throw new Error("useCheckout must be used within a CheckoutProvider");
  }
  return context;
}
